import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  Person,
  SwapHoriz,
  Notifications,
  ShowChart
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useAppData } from '../../context/AppDataContext';
import { useTokenVerification } from '../../hooks/useTokenVerification';
import { getTransferList, getSkuSerialsChart } from '../../services/api';
import { fetchNotifications, normalizeNotificationPayload } from '../../services/notificationService';
import SkeletonLoader from '../Layout/SkeletonLoader';

const formatDateTime = (value) => {
  if (!value) return 'Not available';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Not available';
  }
  return parsed.toLocaleString();
};

// Frontend menu structure with permissions (matching UserManagement)
const FRONTEND_MENUS = [
  {
    id: 'dashboard',
    name: 'Dashboard',
    description: 'Main dashboard and overview',
    visibilityPermission: 'menu:dashboard',
    childPermissions: [
      { id: 'dashboard:view', name: 'Show Dashboard', description: 'View dashboard page' }
    ]
  },
  {
    id: 'inventory-import',
    name: 'Inventory Import',
    description: 'Import inventory orders',
    visibilityPermission: 'menu:inventory-import',
    childPermissions: [
      { id: 'inventory-import:create', name: 'Create Order', description: 'Create new import orders' },
      { id: 'inventory-import:edit', name: 'Edit Order', description: 'Modify existing orders' },
      { id: 'inventory-import:edit-serials', name: 'Edit Serials', description: 'Modify product serials' },
      { id: 'inventory-import:confirm', name: 'Confirm Order', description: 'Confirm import orders' }
    ]
  },
  {
    id: 'inventory-transfer',
    name: 'Inventory Transfer',
    description: 'Transfer management',
    visibilityPermission: 'menu:inventory-transfer',
    childPermissions: [
      { id: 'inventory-transfer:create', name: 'Create Transfer', description: 'Create new transfer orders' },
      { id: 'inventory-transfer:edit-products', name: 'Edit product transfer', description: 'Can edit products in transfer and qty' },
      { id: 'inventory-transfer:edit-serials', name: 'Edit product serials in transfer', description: 'Can add or remove product serials' },
      { id: 'inventory-transfer:update-info', name: 'Update Transfer Info', description: 'Update transfer information' },
      { id: 'inventory-transfer:change-product', name: 'Change Product', description: 'Change products in transfer' },
      { id: 'inventory-transfer:change-serials', name: 'Change Serials', description: 'Change serials in transfer' },
      { id: 'inventory-transfer:confirm-export', name: 'Confirm Export', description: 'Confirm export transfer' },
      { id: 'inventory-transfer:confirm-import', name: 'Confirm Import', description: 'Confirm import transfer' },
      { id: 'inventory-transfer:confirm-transfer-data', name: 'Confirm transfer data', description: 'Can confirm transfer data products and serials' },
      { id: 'inventory-transfer:import-transfer-data', name: 'Confirm product on new client', description: 'Confirm & Import product on new client' },
    ]
  },
  {
    id: 'inventory-inspection',
    name: 'Inventory Count History',
    description: 'View historical inventory counts',
    visibilityPermission: 'menu:inventory-inspection',
    childPermissions: [
      { id: 'inventory-inspection:view-history', name: 'View Count History', description: 'View counting history' }
    ]
  },
  {
    id: 'report',
    name: 'Report',
    description: 'Reports and analytics',
    visibilityPermission: 'menu:report',
    childPermissions: [
      { id: 'report:view', name: 'View Reports', description: 'Access and view reports' }
    ]
  },
  {
    id: 'tag-timeline',
    name: 'Tag Timeline',
    description: 'Tag tracking timeline',
    visibilityPermission: 'menu:tag-timeline',
    childPermissions: [
      { id: 'tag-timeline:view', name: 'View Timeline', description: 'Access tag timeline' }
    ]
  },
  {
    id: 'user-roles-permissions',
    name: 'User, Roles & Permissions',
    description: 'User and permission management',
    visibilityPermission: 'menu:user-roles-permissions',
    childPermissions: [
      { id: 'user-roles-permissions:view', name: 'View Users', description: 'View user list' },
      { id: 'user-roles-permissions:manage', name: 'Manage Permissions', description: 'Manage user permissions' }
    ]
  }
];

// Get permission name/description from permission ID
const getPermissionName = (permissionId) => {
  // Check menu permissions
  for (const menu of FRONTEND_MENUS) {
    if (menu.visibilityPermission === permissionId) {
      return menu.name;
    }
    // Check child permissions
    for (const child of menu.childPermissions) {
      if (child.id === permissionId) {
        return child.name;
      }
    }
  }
  // If not found, return the ID itself
  return permissionId;
};

const Dashboard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [confirmedTransfers, setConfirmedTransfers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loadingTransfers, setLoadingTransfers] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [errorTransfers, setErrorTransfers] = useState(null);
  const [errorNotifications, setErrorNotifications] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  
  // Chart data states
  const [skuChartData, setSkuChartData] = useState([]);
  const [serialChartData, setSerialChartData] = useState([]);
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [errorCharts, setErrorCharts] = useState(null);
  const [clientNames, setClientNames] = useState({});

  const { user, frontendPermissions } = useAppData();
  const navigate = useNavigate();
  
  const CLIENT_IDS = [338614, 338669];

  useTokenVerification(5);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 300);
    return () => clearTimeout(timer);
  }, []);


  useEffect(() => {
    loadConfirmedTransfers();
    loadNotifications();
    loadChartData();
  }, []);

  const loadChartData = async () => {
    setLoadingCharts(true);
    setErrorCharts(null);
    
    try {
      const [response1, response2] = await Promise.all([
        getSkuSerialsChart(CLIENT_IDS[0]),
        getSkuSerialsChart(CLIENT_IDS[1])
      ]);
      
      // Store client names
      if (response1?.clientName) {
        setClientNames(prev => ({ ...prev, [CLIENT_IDS[0]]: response1.clientName }));
      }
      if (response2?.clientName) {
        setClientNames(prev => ({ ...prev, [CLIENT_IDS[1]]: response2.clientName }));
      }
      
      // Combine data from both clients by date
      const data1 = response1?.chartData || [];
      const data2 = response2?.chartData || [];
      
      // Get all unique dates
      const allDates = new Set();
      data1.forEach(item => allDates.add(item.date));
      data2.forEach(item => allDates.add(item.date));
      const sortedDates = Array.from(allDates).sort();
      
      // Create merged data for SKU chart
      const skuData = sortedDates.map(date => {
        const item1 = data1.find(d => d.date === date);
        const item2 = data2.find(d => d.date === date);
        return {
          date,
          [`client_${CLIENT_IDS[0]}`]: item1?.total_sku || null,
          [`client_${CLIENT_IDS[1]}`]: item2?.total_sku || null
        };
      });
      
      // Create merged data for Serial chart
      const serialData = sortedDates.map(date => {
        const item1 = data1.find(d => d.date === date);
        const item2 = data2.find(d => d.date === date);
        return {
          date,
          [`client_${CLIENT_IDS[0]}`]: item1?.total_serial_count || null,
          [`client_${CLIENT_IDS[1]}`]: item2?.total_serial_count || null
        };
      });
      
      setSkuChartData(skuData);
      setSerialChartData(serialData);
    } catch (err) {
      setErrorCharts(err.message || 'Failed to load chart data');
      setSkuChartData([]);
      setSerialChartData([]);
    } finally {
      setLoadingCharts(false);
    }
  };
  
  const formatChartDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const loadConfirmedTransfers = async () => {
    setLoadingTransfers(true);
    setErrorTransfers(null);
    try {
      const response = await getTransferList({
        status: 'confirmed',
        page: 1,
        limit: 100
      });
      
      if (response && response.data) {
        setConfirmedTransfers(response.data);
      } else {
        setConfirmedTransfers([]);
      }
    } catch (err) {
      setErrorTransfers(err.message || 'Failed to load confirmed transfers');
      setConfirmedTransfers([]);
    } finally {
      setLoadingTransfers(false);
    }
  };

  const loadNotifications = async () => {
    setLoadingNotifications(true);
    setErrorNotifications(null);
    try {
      const response = await fetchNotifications({});
      const items = normalizeNotificationPayload(response);
      // Get top 10 most recent notifications
      const sorted = items
        .sort((a, b) => {
          const dateA = new Date(a.timestamp || a.createdAt || 0);
          const dateB = new Date(b.timestamp || b.createdAt || 0);
          return dateB - dateA;
        })
        .slice(0, 10);
      setNotifications(sorted);
    } catch (err) {
      setErrorNotifications(err.message || 'Failed to load notifications');
      setNotifications([]);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleTransferClick = (transfer) => {
    const transferId = transfer._id || transfer.id;
    if (transferId) {
      navigate(`/inventory-transfer/${transferId}`);
    }
  };

  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification);
    setNotificationDialogOpen(true);
  };

  const handleCloseNotificationDialog = () => {
    setNotificationDialogOpen(false);
    setSelectedNotification(null);
  };

  return (
    <Box sx={{ px: { xs: 0, sm: 1 } }}>
      {isLoading ? (
        <SkeletonLoader type="stats" count={3} />
      ) : (
        <Grid container spacing={3}>
          {/* Current User and Frontend Permissions */}
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0', height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#0ea5e9', width: 48, height: 48 }}>
                    <Person sx={{ color: '#fff' }} />
                  </Avatar>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
                    Current User
                  </Typography>
                </Stack>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                    Signed in as
              </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {user?.username || user?.name || 'Unknown user'}
              </Typography>
                  {user?.userId && (
              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      User ID: {user.userId}
              </Typography>
                  )}
            </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Frontend Permissions
                </Typography>
                {frontendPermissions && frontendPermissions.length > 0 ? (
                  <Box sx={{ flex: 1, overflow: 'auto', maxHeight: '200px' }}>
                    <Stack spacing={1}>
                      {frontendPermissions.map((permission) => (
                        <Typography 
                          key={permission} 
                          variant="body2" 
              sx={{
                            py: 0.5,
                            px: 1,
                            borderRadius: 1,
                            bgcolor: '#f8fafc',
                border: '1px solid #e2e8f0'
              }}
            >
                          {getPermissionName(permission)}
                        </Typography>
                      ))}
                    </Stack>
          </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                    No frontend permissions assigned to this user.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Confirmed Transfer Orders */}
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0', height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexShrink: 0 }}>
                  <Avatar sx={{ bgcolor: '#22c55e', width: 48, height: 48 }}>
                    <SwapHoriz sx={{ color: '#fff' }} />
                  </Avatar>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
                    Confirmed Transfer Orders
                  </Typography>
                </Stack>

                {loadingTransfers ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, flexShrink: 0 }}>
                    <CircularProgress />
                  </Box>
                ) : errorTransfers ? (
                  <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
                    {errorTransfers}
                  </Alert>
                ) : confirmedTransfers.length === 0 ? (
                  <Typography variant="body2" sx={{ color: '#94a3b8', flexShrink: 0 }}>
                    No confirmed transfer orders found.
                  </Typography>
                ) : (
                  <TableContainer 
                    component={Paper} 
                    variant="outlined" 
                    sx={{ 
                      flex: 1,
                      minHeight: 0,
                      maxHeight: '200px',
                      overflow: 'auto',
                      display: 'block',
                      height: '100%'
                    }}
                  >
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Transfer Code</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>From</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>To</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {confirmedTransfers.map((transfer) => (
                          <TableRow 
                            key={transfer._id || transfer.id} 
                            hover
                            onClick={() => handleTransferClick(transfer)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>{transfer.transferCode || transfer.code || '—'}</TableCell>
                            <TableCell>{transfer.fromBranchName || transfer.fromLocation || '—'}</TableCell>
                            <TableCell>{transfer.toBranchName || transfer.toLocation || '—'}</TableCell>
                            <TableCell>
                              <Chip 
                                label="Waiting" 
                                size="small" 
                                color="warning"
                                sx={{ textTransform: 'capitalize' }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
              </Grid>

          {/* Top 10 Recent Notifications */}
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0', height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexShrink: 0 }}>
                  <Avatar sx={{ bgcolor: '#f97316', width: 48, height: 48 }}>
                    <Notifications sx={{ color: '#fff' }} />
                  </Avatar>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
                    Top 10 Recent Notifications
                  </Typography>
                </Stack>

                {loadingNotifications ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, flexShrink: 0 }}>
                    <CircularProgress />
                  </Box>
                ) : errorNotifications ? (
                  <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
                    {errorNotifications}
                  </Alert>
                ) : notifications.length === 0 ? (
                  <Typography variant="body2" sx={{ color: '#94a3b8', flexShrink: 0 }}>
                    No notifications found.
                  </Typography>
                ) : (
                  <TableContainer 
                    component={Paper} 
                    variant="outlined" 
                    sx={{ 
                      flex: 1,
                      minHeight: 0,
                      maxHeight: '390px',
                      overflow: 'auto',
                      display: 'block',
                      height: '100%'
                    }}
                  >
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Level</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {notifications.map((notification, index) => (
                          <TableRow 
                            key={notification._id || notification.id || index} 
                            hover
                            onClick={() => handleNotificationClick(notification)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: '200px'
                                }}
                              >
                                {notification.message || notification.content || notification.title || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={notification.level || 'info'} 
                                size="small" 
                                color={
                                  notification.level === 'error' ? 'error' :
                                  notification.level === 'warning' ? 'warning' :
                                  notification.level === 'success' ? 'success' : 'default'
                                }
                                sx={{ textTransform: 'capitalize' }}
                              />
                            </TableCell>
                            <TableCell>
                              {formatDateTime(notification.timestamp || notification.createdAt || notification.date)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Charts Section */}
          <Grid container spacing={3} sx={{ mt: 0 }}>
            {/* Chart 1 - SKU Count by date */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0', height: '100%' }}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <Avatar sx={{ bgcolor: '#3b82f6', width: 48, height: 48 }}>
                      <ShowChart sx={{ color: '#fff' }} />
                    </Avatar>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
                      SKU Count by date
                    </Typography>
                  </Stack>

                  {loadingCharts ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                      <CircularProgress />
                    </Box>
                  ) : errorCharts ? (
                    <Alert severity="error">
                      {errorCharts}
                    </Alert>
                  ) : skuChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={skuChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={formatChartDate}
                          angle={-45}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis />
                        <Tooltip 
                          labelFormatter={(value) => formatChartDate(value)}
                          formatter={(value, name) => {
                            const clientId = name.replace('client_', '');
                            const clientName = clientNames[clientId] || `Client ${clientId}`;
                            return [value, clientName];
                          }}
                        />
                        <Legend 
                          formatter={(value) => {
                            const clientId = value.replace('client_', '');
                            return clientNames[clientId] || `Client ${clientId}`;
                          }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey={`client_${CLIENT_IDS[0]}`}
                          stroke="#3b82f6" 
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          name={`client_${CLIENT_IDS[0]}`}
                        />
                        <Line 
                          type="monotone" 
                          dataKey={`client_${CLIENT_IDS[1]}`}
                          stroke="#10b981" 
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          name={`client_${CLIENT_IDS[1]}`}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#94a3b8', textAlign: 'center', py: 4 }}>
                      No chart data available
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Chart 2 - Serial Count By Date */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0', height: '100%' }}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <Avatar sx={{ bgcolor: '#8b5cf6', width: 48, height: 48 }}>
                      <ShowChart sx={{ color: '#fff' }} />
                    </Avatar>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
                      Serial Count By Date
                    </Typography>
                  </Stack>

                  {loadingCharts ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                      <CircularProgress />
                    </Box>
                  ) : errorCharts ? (
                    <Alert severity="error">
                      {errorCharts}
                    </Alert>
                  ) : serialChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={serialChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={formatChartDate}
                          angle={-45}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis />
                        <Tooltip 
                          labelFormatter={(value) => formatChartDate(value)}
                          formatter={(value, name) => {
                            const clientId = name.replace('client_', '');
                            const clientName = clientNames[clientId] || `Client ${clientId}`;
                            return [value, clientName];
                          }}
                        />
                        <Legend 
                          formatter={(value) => {
                            const clientId = value.replace('client_', '');
                            return clientNames[clientId] || `Client ${clientId}`;
                          }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey={`client_${CLIENT_IDS[0]}`}
                          stroke="#3b82f6" 
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          name={`client_${CLIENT_IDS[0]}`}
                        />
                        <Line 
                          type="monotone" 
                          dataKey={`client_${CLIENT_IDS[1]}`}
                          stroke="#10b981" 
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          name={`client_${CLIENT_IDS[1]}`}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#94a3b8', textAlign: 'center', py: 4 }}>
                      No chart data available
                      </Typography>
                    )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      )}

      {/* Notification Detail Dialog */}
      <Dialog 
        open={notificationDialogOpen} 
        onClose={handleCloseNotificationDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Notification Details
        </DialogTitle>
        <DialogContent>
          {selectedNotification && (
            <Box>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                Level
              </Typography>
              <Chip 
                label={selectedNotification.level || 'info'} 
                size="small" 
                color={
                  selectedNotification.level === 'error' ? 'error' :
                  selectedNotification.level === 'warning' ? 'warning' :
                  selectedNotification.level === 'success' ? 'success' : 'default'
                }
                sx={{ textTransform: 'capitalize', mb: 2 }}
              />
              
              <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                Time
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {formatDateTime(selectedNotification.timestamp || selectedNotification.createdAt || selectedNotification.date)}
                  </Typography>

              <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                Message
                  </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {selectedNotification.message || selectedNotification.content || selectedNotification.title || '—'}
                  </Typography>
          </Box>
      )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNotificationDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
